import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { 
  ClipboardList, 
  Play, 
  History, 
  CheckCircle2, 
  AlertCircle,
  User
} from "lucide-react";

export function TestHomeView({ 
  planType, 
  testHistory, 
  onNewTest 
}) {
  const navigate = useNavigate();

  return (
    <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Start Test Card */}
      <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-0 shadow-lg">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold mb-2">Ready to Test Your Knowledge?</h2>
              <p className="text-emerald-100">Take a timed practice test and track your progress</p>
            </div>
            <Button
              className="bg-white text-emerald-600 hover:bg-emerald-50"
              onClick={onNewTest}
              data-testid="new-test-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              New Test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test History */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-600" />
            Recent Tests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {testHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No tests taken yet</p>
              <p className="text-sm">Start your first practice test!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {testHistory.map((test) => (
                <div
                  key={test.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${test.percentage >= 60 ? "bg-green-100" : "bg-amber-100"}`}>
                      {test.percentage >= 60 ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{test.subject}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(test.submitted_at).toLocaleDateString()} 
                        {test.time_taken_minutes && ` • ${test.time_taken_minutes.toFixed(0)} min`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-gray-900">
                      {test.score}/{test.num_questions}
                    </p>
                    <Badge className={test.percentage >= 60 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                      {test.percentage?.toFixed(0)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Back to Profile Button */}
      <div className="flex justify-center pt-4">
        <Button
          className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700"
          onClick={() => navigate("/student/profile")}
          data-testid="back-to-profile-btn"
        >
          <User className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>
      </div>
    </main>
  );
}

export default TestHomeView;
